'use client';

import React, { useEffect, useRef } from 'react';


export default function TiersSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef?.current?.querySelectorAll('.rev');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24">
      <div className="max-w-site mx-auto px-7">
        {/* Price hero: main card + vs card */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-7 items-stretch">
          {/* Main price card */}
          <div
            className="rev relative rounded-2xl p-11 text-cream"
            style={{
              background: 'var(--navy-deep)',
              boxShadow: '0 30px 60px rgba(18,36,47,0.35)',
            }}
          >
            <div className="absolute -top-3.5 left-9 bg-primary text-navy-deep font-mono text-[11px] font-semibold tracking-[0.16em] uppercase px-3.5 py-1.5 rounded-lg">
              Per dedicated rep
            </div>
            <div className="font-display font-bold text-white leading-none" style={{ fontSize: 'clamp(60px,6vw,84px)' }}>
              $3,499
              <small className="font-mono text-[14px] text-muted-cream font-normal tracking-[0.06em]"> /month</small>
            </div>
            <ul className="mt-6 mb-7 space-y-0">
              {[
                'One named, full-time, dedicated SDR per seat',
                'Cold calling first: dialer and verified mobile data included',
                'Email and LinkedIn touches to supplement the phone',
                'Signal-based account targeting, scored weekly',
                'Weekly full-funnel reporting',
                'Written qualified-meeting definition, with a per-meeting fee set in your agreement',
                'Month to month. Paid upfront each month. Cancel with 30 days notice.',
              ]?.map((item) => (
                <li key={item} className="py-2 pl-7 relative text-[15px] text-muted-cream" style={{ listStyle: 'none' }}>
                  <span
                    className="absolute left-0 top-3.5"
                    style={{
                      width: '13px',
                      height: '13px',
                      borderRadius: '4px',
                      background: 'var(--gold)',
                      clipPath: 'polygon(14% 44%,0 60%,40% 100%,100% 16%,84% 2%,40% 70%)',
                      display: 'block',
                    }}
                  />
                  {item}
                </li>
              ))}
            </ul>
            <a
              href="/request-meeting"
              className="flex justify-center font-semibold text-sm px-6 py-3.5 rounded-[10px] bg-primary text-navy-deep border border-transparent transition-all duration-200 hover:bg-gold-bright hover:-translate-y-px hover:shadow-gold w-full"
            >
              Book a strategy call
            </a>
          </div>

          {/* VS card */}
          <div
            className="rev bg-white border border-line-color rounded-2xl p-11 flex flex-col justify-center"
          >
            <div className="eyebrow">The alternative</div>
            <div
              className="font-display font-bold leading-none"
              style={{
                fontSize: 'clamp(48px,4.6vw,64px)',
                color: 'var(--muted)',
                textDecoration: 'line-through',
                textDecorationColor: 'rgba(201,137,65,0.7)',
                textDecorationThickness: '5px',
              }}
            >
              ~$10,000
              <small className="font-mono text-[14px] font-normal tracking-[0.06em]"> /month</small>
            </div>
            <p className="mt-2.5 font-semibold text-ink">One US in-house SDR, fully loaded.</p>
            <ul className="mt-5 space-y-0">
              {[
                { label: 'Base salary (median)', val: '$4,600 /mo' },
                { label: 'Variable compensation', val: '$1,700 /mo' },
                { label: 'Benefits and payroll taxes', val: '$1,600 /mo' },
                { label: 'Data, tools, and phone stack', val: '$800 /mo' },
                { label: 'Management time', val: '$900 /mo' },
                { label: 'Ramp amortization', val: '$700 /mo' },
              ]?.map((row) => (
                <li
                  key={row?.label}
                  className="py-1.5 text-[14.5px] text-muted-text border-b border-dashed border-line-color flex justify-between gap-4 last:border-0"
                  style={{ listStyle: 'none' }}
                >
                  <span>{row?.label}</span>
                  <b className="font-mono text-ink">{row?.val}</b>
                </li>
              ))}
            </ul>
            <p className="mt-3.5 text-[13px] text-muted-text">
              Before turnover. SDR turnover runs 34 to 40% annually, and each departure costs $78K or more to replace.
            </p>
          </div>
        </div>

        {/* Scale row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
          {[
            { label: 'One rep', price: '$3,499 /mo', desc: 'Prove the channel and tune the message.' },
            { label: 'Two reps', price: '$6,998 /mo', desc: 'Split territories or personas. Compare and double down.' },
            { label: 'Three reps', price: '$10,497 /mo', desc: 'A full outbound team for about the cost of one US hire.' },
          ]?.map((s, i) => (
            <div
              key={s?.label}
              className="rev bg-white border border-line-color rounded-brand p-6 text-center"
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              <i className="font-mono text-[12px] text-primary font-normal not-italic letter-spacing-[0.12em] uppercase">{s?.label}</i>
              <b className="font-display text-[34px] text-ink block font-bold">{s?.price}</b>
              <span className="block text-[13px] text-muted-text mt-2">{s?.desc}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}